import type { AxiosInstance } from 'axios';

// Helper function to wait for a specified time (ms)
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export class TextToSpeechClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  fetchTextToSpeechAudio = async (text: string): Promise<string> => {
    // If the text is long, use the asynchronous endpoint
    if (text.length > 2800) {
      // Start the asynchronous task
      const asyncResponse = await this.authenticatedClient.post('/chat/vocalize-async', { text });
      console.log('Async TTS start response:', asyncResponse.data); // Log the start response
      if (!asyncResponse.data.success || !asyncResponse.data.data.taskId) {
        throw new Error('Invalid asynchronous TTS data received');
      }

      const taskId = asyncResponse.data.data.taskId;
      let outputUri: string | undefined;
      let attempts = 0;
      let delay = 1000; // start with 1 second
      const maxAttempts = 20;

      // Poll until the task is completed or we exceed maxAttempts
      while (attempts < maxAttempts) {
        await wait(delay);
        const pollResponse = await this.authenticatedClient.get(`/chat/vocalize-async/${taskId}`);
        console.log(`Polling attempt ${attempts + 1} (delay ${delay}ms):`, pollResponse.data);
        if (!pollResponse.data.success) {
          throw new Error('Error polling asynchronous TTS task');
        }

        const taskData = pollResponse.data.data;
        console.log('Current task data:', taskData); // Detailed log of the task data
        if (taskData.TaskStatus === 'completed' && taskData.OutputUri) {
          outputUri = taskData.OutputUri;
          console.log('Task completed with output URI:', outputUri);
          break;
        } else if (taskData.TaskStatus === 'failed') {
          throw new Error('Asynchronous TTS task failed');
        }
        attempts++;
        delay = Math.min(delay * 2, 10000); // double the delay, capping at 10 seconds
      }

      if (!outputUri) {
        throw new Error('Text-to-speech processing did not complete in time.');
      }
      console.log('Final output URI from polling:', outputUri);
      return outputUri;
    } else {
      // For shorter texts, use the synchronous endpoint
      const response = await this.authenticatedClient.post('/chat/vocalize', { queryIdentifier: text });
      if (response.data.success && response.data.data.audio) {
        return response.data.data.audio;
      }
      throw new Error('Invalid audio data received');
    }
  };
}

export default TextToSpeechClient;
